'use client';

/**
 * ModeTileCard — generic mode-launcher tile.
 *
 * Parameterised via `target_mode` placement prop. The preset's
 * canvas_placements list owns which modes appear on the home grid;
 * this card just renders the tile for whichever target it's mounted
 * with. Reads icon/label/description from the matrix nav (server-
 * driven), clicks call `setActiveExplorerView(target_mode)`.
 *
 * @see klyde_edge/composition/cards/home.py — content_key="mode_tile"
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Home
 * @layer: presentation
 */

import {
  ChevronRight,
  FolderKanban, Factory, Sparkles, FileText, Package,
  MessageSquare, HelpCircle, Compass, ClipboardPen, LayoutGrid,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { ExplorerView } from '@/lib/stores/layout-store';
import { switchExplorerMode } from '@/lib/stores/tab-store';
import { useNavItems } from '@/lib/stores/total-matrix-store';
import { text, surfaceBase } from '@/lib/design/tokens';
import { CardShell } from '../card-shell';

const ICON_MAP: Record<string, LucideIcon> = {
  FolderKanban, Factory, Sparkles, FileText, Package,
  MessageSquare, HelpCircle, Compass, ClipboardPen, LayoutGrid,
};

interface ModeTileCardProps {
  target_mode?: string;
}

export function ModeTileCard({ target_mode }: ModeTileCardProps) {
  const navItems = useNavItems();
  const item = target_mode ? navItems.find((n) => n.mode === target_mode) : undefined;

  if (!item) return null;

  const Icon = ICON_MAP[item.icon] ?? HelpCircle;

  return (
    <CardShell
      as="button"
      interactive
      onClick={() => switchExplorerMode(item.mode as ExplorerView)}
      style={{ fontSize: surfaceBase.spacious }}
      className="group relative flex flex-col items-start gap-3 p-4 w-full h-full text-left"
    >
      <div className="p-2 rounded-lg bg-primary/10">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="text-left">
        <p className={text.title}>{item.label}</p>
        {item.description && (
          <p className={cn(text.muted, 'mt-0.5')}>{item.description}</p>
        )}
      </div>
      <ChevronRight className="absolute top-4 right-3 w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
    </CardShell>
  );
}
